/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useEffect, useRef, useState} from 'react'
import {defineMessage, useIntl} from 'react-intl'

import PropTypes from 'prop-types'
import {
    Grid,
    GridItem,
    SimpleGrid,
    Stack
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {getAppOrigin} from '@salesforce/pwa-kit-react-sdk/utils/url'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import useAddressFields from '../../components/forms/useAddressFields'
import Field from '../../components/field'
import {useCurrentCustomer} from '@epmc-mach/accelarator/overrides/app/hooks/use-current-customer'
import SwitchSiteConfirmationDialog from '../../pages/checkout/partials/switch-to-site'
import {useCustomerLocation} from '../../contexts/CustomerLocation'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'

const defaultFormTitleAriaLabel = defineMessage({
    defaultMessage: 'Address Form',
    id: 'use_address_fields.label.address_form'
})

const detectEnvironment = (appOrigin, domainData) => {
    for (const country in domainData) {
        const envs = domainData[country]
        for (const env in envs) {
            const {domain, mrtDomain} = envs[env]
            if (
                (domain && appOrigin.includes(domain)) ||
                (mrtDomain && appOrigin.includes(mrtDomain))
            ) {
                return env
            }
        }
    }
    return 'production' // fallback default environment
}

const AddressFields = ({
    form,
    isNewAddress,
    prefix = '',
    formTitleAriaLabel = defaultFormTitleAriaLabel,
    isBillingAddress = false
}) => {
    const {data: customer} = useCurrentCustomer()
    const {fields, isProgrammaticCountryChange} = useAddressFields({
        form,
        prefix: isBillingAddress ? 'billing_' : prefix
    })
    const addressFormRef = useRef()
    const {setLocaleCookie, defaultCountryCode, hasDefaultCountryCodeLoaded} = useCustomerLocation()
    const {locale: originalLocale, site: originalSite, buildUrl} = useMultiSite()

    const {watch, setValue, clearErrors, trigger} = form

    const initialCountry = watch(`${prefix}countryCode`) || defaultCountryCode
    const prevCountryRef = useRef(hasDefaultCountryCodeLoaded ? initialCountry : undefined)
    const [showDialog, setShowDialog] = useState(false)

    // Watch for changes to countryCode
    useEffect(() => {
        const subscription = watch((value, {name}) => {
            if (name === `${prefix}countryCode`) {
                const newCountry = value?.[`${prefix}countryCode`]

                if (isProgrammaticCountryChange?.current && prevCountryRef.current === undefined) {
                    prevCountryRef.current = newCountry
                    return
                }

                if (newCountry && newCountry !== prevCountryRef.current) {
                    if (!isBillingAddress) {
                        setShowDialog(true)
                    } else {
                        setValue(`${prefix}stateCode`, '')
                        setValue(`${prefix}postalCode`, '')
                        clearErrors([`${prefix}stateCode`, `${prefix}postalCode`])
                        prevCountryRef.current = newCountry
                    }
                }
            }
        })

        return () => subscription.unsubscribe()
    }, [watch, prefix, isBillingAddress, setValue, clearErrors])

    const handleDialogConfirm = () => {
        const {sites = [], domains = []} = getConfig().app || {}

        const selectedCountry = form.getValues(`${prefix}countryCode`)?.toUpperCase()
        const site =
            sites.find((s) => s.id === selectedCountry) || sites.find((s) => s.id === 'INT')
        const locale = site?.l10n?.supportedLocales?.[0]

        const appOrigin = getAppOrigin().toLowerCase()
        const domainData = domains?.[0] || {}

        setLocaleCookie(site.id, locale.id, selectedCountry)

        // Handle localhost redirect separately
        if (appOrigin.includes('localhost')) {
            window.location.href = `${appOrigin}${buildUrl('/cart')}`
            prevCountryRef.current = selectedCountry
            setShowDialog(false)
            return
        }

        const env = detectEnvironment(appOrigin, domainData)

        // Get target domain for selected country in detected env
        const envDomains = domainData[selectedCountry]?.[env] || domainData['INT']?.[env]

        if (!envDomains) {
            console.warn(`No domain mapping found for ${selectedCountry} in ${env}`)
            setShowDialog(false)
            return
        }

        const isMrtDomain = appOrigin.includes('mobify-storefront.com')

        const targetDomain = isMrtDomain ? envDomains.mrtDomain : envDomains.domain

        // Redirect to full domain URL
        const redirectUrl = `https://${targetDomain}/${locale.alias}/cart`

        // Reset state and postal code fields before redirect
        setValue(`${prefix}stateCode`, '')
        setValue(`${prefix}postalCode`, '')
        clearErrors([`${prefix}stateCode`, `${prefix}postalCode`])

        prevCountryRef.current = selectedCountry
        setShowDialog(false)

        window.location.href = isMrtDomain
            ? redirectUrl
            : `${appOrigin}/on/demandware.store/Sites-${
                  originalSite.id
              }-Site/${originalLocale.id.replace('-', '_')}/Home-SwitchToSite?toLocale=${
                  locale.id
              }&toSite=${site.id}&toCountry=${selectedCountry}&toUrl=${redirectUrl}`
    }

    const handleDialogClose = () => {
        setTimeout(() => {
            // Revert to previous value when user cancels dialog
            const revertCountry = prevCountryRef.current || defaultCountryCode
            prevCountryRef.current = revertCountry
            setValue(`${prefix}countryCode`, revertCountry)
            // Clear any validation errors for countryCode
            clearErrors([`${prefix}countryCode`, `${prefix}phone`])
            // Trigger both countryCode and phone to refresh rules
            trigger([`${prefix}countryCode`, `${prefix}phone`])

            setShowDialog(false)
        }, 0)
    }

    return (
        <>
            <Stack
                spacing={5}
                aria-label={formTitleAriaLabel}
                tabIndex={0}
                role="form"
                ref={addressFormRef}
            >
                <SimpleGrid columns={[1, 1, 2]} gap={5}>
                    <Field {...fields.firstName} />
                    <Field {...fields.lastName} />
                </SimpleGrid>
                <Field {...fields.address1} />
                <Grid templateColumns="repeat(8, 1fr)" gap={5} alignItems="start">
                    <GridItem colSpan={[4, 4, 4]}>
                        <Field {...fields.address2} />
                    </GridItem>
                    <GridItem colSpan={[4, 4, 4]}>
                        <Field {...fields.c_address3} />
                    </GridItem>
                </Grid>
                <Grid templateColumns="repeat(8, 1fr)" gap={5}>
                    <GridItem colSpan={[8, 8, 4]}>
                        <Field {...fields.postalCode} />
                    </GridItem>
                    <GridItem colSpan={[8, 8, 4]}>
                        <Field {...fields.city} />
                    </GridItem>
                </Grid>
                <Field {...fields.countryCode} isDisabled={isBillingAddress} />
                <Field {...fields.phone} />
                <Field {...fields.companyName} />
                {customer?.isRegistered && !isBillingAddress && <Field {...fields.preferred} />}
            </Stack>

            {!isBillingAddress && (
                <SwitchSiteConfirmationDialog
                    isOpen={showDialog}
                    onConfirm={handleDialogConfirm}
                    onClose={handleDialogClose}
                />
            )}
        </>
    )
}

AddressFields.propTypes = {
    /** Object returned from `useForm` */
    form: PropTypes.object.isRequired,

    /** Optional prefix for field names */
    prefix: PropTypes.string,

    /** Optional aria label to use for the address form */
    formTitleAriaLabel: PropTypes.string,

    /** Optional flag to indication if an address is a billing address */
    isBillingAddress: PropTypes.bool,
    isNewAddress: PropTypes.bool
}

export default AddressFields
